import DeleteIcon from '@mui/icons-material/Delete';
import NotesIcon from '@mui/icons-material/Notes';

import { capture } from '@/handlers/error';
import { useOutsideClick } from '@/hooks/useOutsideClick';
import { ResizeBorder } from '@/pages/LessonEditor/components/ResizeBorder';
import { useComponentEditor } from '@/providers/editor/EditorProvider';
import { Coordinate, isSlideComponentSentenceMaker, PropsWithPreview, Size, SlideComponent } from '@/types/slide';
import { Box, Typography } from '@mui/material';
import { useNotifications } from '@toolpad/core';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'rooks';
import { ComponentLabel } from '../ComponentLabel';
import { ContextualBar } from '../ContextualBar';
import { useRightClickMenu } from '../hooks/useRightClickMenu';
import { useSlideComponent } from '../hooks/useSlideComponent';
import { RightClickMenu } from '../RightClickMenu';
import { SlideRef } from '../Slide';
import { Grid } from './Grid';
import { SettingsPopover } from './SettingsPopover';

interface Props {
    ref: React.RefObject<HTMLDivElement | null>;
    init: {
        model: SlideComponent;
        focus?: boolean;
    };
    slideRef: React.RefObject<SlideRef | null>;
}

export const SlideComponentSentenceMaker: React.FC<PropsWithPreview<Props>> = ({ ref, init, preview, slideRef }) => {
    const { t } = useTranslation();
    const notifications = useNotifications();
    const { deleteComponent, updateComponent, bringToFront, bringToBack, copyComponent, pasteComponent, undo, redo } = useComponentEditor();

    const { rightClickMenu, handleRightClickMenu, rightClickMenuClose } = useRightClickMenu();

    // Add refs for the popovers
    const settingRef = useRef<HTMLDivElement | null>(null);

    const { isSelected } = useSlideComponent(ref, [], !!preview);

    const [model, setModel] = useState<SlideComponent>({ ...init.model });
    const [isFocus, setIsFocus] = useState<boolean>(init.focus ?? false);

    const [tSize, setTSize] = useState<Size>(model.size);
    const [tPos, setTPos] = useState<Coordinate>(model.pos);

    const [aspectRatio] = useState(model.size.width / model.size.height);

    // Add state for image picker popover
    const [settingAnchorEl, setSettingAnchorEl] = useState<HTMLElement | null>(null);

    const isEditing = isFocus || isSelected;

    const isContentValid = useMemo(() => {
        if (!isSlideComponentSentenceMaker(model)) return false;
        const { keywords, sentencePattern, theme, writingTechnique, rhetoric } = model.content;
        return (keywords && keywords.length > 0) || !!sentencePattern || !!theme || !!writingTechnique || !!rhetoric;
    }, [model]);

    const showWarning = isSlideComponentSentenceMaker(model) && (!model.content.difficulty || !isContentValid);

    const onDeleteComponent = React.useCallback(
        async (id: string) => {
            try {
                await deleteComponent(id);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
            }
        },
        [deleteComponent, notifications],
    );

    const onUpdateComponent = React.useCallback(
        async (id: string, component: SlideComponent) => {
            try {
                await updateComponent(id, component);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
            }
        },
        [updateComponent, notifications],
    );

    const onBringToFrontHandler = React.useCallback(
        async (id: string) => {
            try {
                await bringToFront(id);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
            }
        },
        [bringToFront, notifications],
    );

    const onBringToBackHandler = React.useCallback(
        async (id: string) => {
            try {
                await bringToBack(id);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
            }
        },
        [bringToBack, notifications],
    );

    const debouncedUpdateComponent = useDebounce(onUpdateComponent, 500);

    useOutsideClick(ref, () => {
        if (preview) return;
        if (!ref) return;

        setIsFocus(false);
    });

    if (!isSlideComponentSentenceMaker(model)) {
        return null;
    }

    return (
        <Box
            onContextMenu={isEditing ? handleRightClickMenu : undefined}
            ref={ref}
            id={`sentence-maker-${model.id}`}
            sx={{
                position: 'absolute',
                left: tPos.x,
                top: tPos.y,
                m: 'inherit',
                p: 'inherit',
                textAlign: 'left',
                width: tSize.width,
                height: tSize.height,
            }}
            onClick={(e) => {
                if (!preview) {
                    e.stopPropagation();
                }
            }}
            onDoubleClick={() => {
                if (preview) return;
                setSettingAnchorEl(ref.current);
            }}
        >
            <ResizeBorder
                size={tSize}
                pos={tPos}
                preview={preview}
                isSelected={isEditing}
                onResize={(size, pos) => {
                    setTSize(size);
                    setTPos(pos);
                    const updateModel = { ...model, size, pos };
                    setModel({ ...updateModel });
                    debouncedUpdateComponent?.(model.id, { ...updateModel });
                }}
                aspectRatio={aspectRatio}
                slideRef={slideRef}
                resizable={false}
            >
                {!preview && (
                    <ComponentLabel
                        variant='caption'
                        sx={{
                            ...(!isEditing && {
                                opacity: 0, // Hide by default
                                transition: 'opacity 0.2s ease',
                                [`#sentence-maker-${model.id}:hover &`]: {
                                    opacity: 1, // Show when text-component is hovered
                                },
                            }),
                        }}
                        noWrap
                    >
                        {t('slide.components.sentenceMaker.title')}
                    </ComponentLabel>
                )}

                <Box
                    sx={{
                        width: tSize.width,
                        height: tSize.height,
                        aspectRatio: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}
                >
                    <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'start' }}>
                        <img src='/platform/images/sentenceMaker.svg' alt='Sentence Maker' style={{ width: 96, height: 96, objectFit: 'contain' }} />
                    </Box>
                    <Grid cols={model.content.size.cols} rows={model.content.size.rows} language={model.content.language} />
                </Box>

                {showWarning && (
                    <Typography
                        variant='body2'
                        sx={{
                            color: (theme) => (theme.vars || theme).palette.error.main,
                            mt: 1,
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            width: '100%',
                        }}
                    >
                        {t('slide.components.sentenceMaker.warning.notAssigned')}
                    </Typography>
                )}

                {!preview && isEditing && (
                    <ContextualBar
                        startButtons={[
                            {
                                icon: <NotesIcon />,
                                tooltip: t('slide.components.sentenceMaker.tooltip.edit'),
                                onClick: () => {
                                    setSettingAnchorEl(ref.current);
                                },
                            },
                        ]}
                        endButtons={[
                            {
                                icon: <DeleteIcon sx={{ color: (theme) => (theme.vars || theme).palette.error.main }} />,
                                tooltip: t('slide.components.sentenceMaker.tooltip.remove'),
                                onClick: () => {
                                    onDeleteComponent?.(model.id);
                                },
                            },
                        ]}
                    />
                )}

                {settingAnchorEl && (
                    <SettingsPopover
                        anchorEl={settingAnchorEl}
                        slotProps={{
                            paper: {
                                ref: settingRef, // Attach the ref to the Paper element
                                sx: {
                                    p: 2,
                                },
                            },
                        }}
                        value={{
                            size: model.content.size,
                            maxWords: model.content.maxWords,
                            difficulty: model.content.difficulty,
                            language: model.content.language,
                            keywords: model.content.keywords,
                            sentencePattern: model.content.sentencePattern,
                            theme: model.content.theme,
                            writingTechnique: model.content.writingTechnique,
                            rhetoric: model.content.rhetoric,
                        }}
                        onClose={() => {
                            setSettingAnchorEl(null);
                        }}
                        onUpdate={(settings) => {
                            const updatedContent = { ...model.content, ...settings };
                            setModel({ ...model, content: updatedContent });
                            debouncedUpdateComponent?.(model.id, { ...model, content: updatedContent });
                        }}
                    />
                )}
            </ResizeBorder>

            <RightClickMenu
                componentId={model.id}
                contextMenu={rightClickMenu}
                handleClose={rightClickMenuClose}
                bringToFront={onBringToFrontHandler}
                bringToBack={onBringToBackHandler}
                copyComponent={copyComponent}
                pasteComponent={pasteComponent}
                undo={undo}
                redo={redo}
            />
        </Box>
    );
};
